// stores/auth.js
import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import apiClient from '@/axios';

export const useAuthStore = defineStore('auth', () => {
    // State
    const token = ref(localStorage.getItem('auth_token') || null);
    const shopId = ref(localStorage.getItem('shop_id') || null);
    const shopName = ref(localStorage.getItem('shop_name') || null);
    const shopImage = ref(localStorage.getItem('shop_image') || null);
    const adminId = ref(localStorage.getItem('admin_id') || null);
    const adminName = ref(localStorage.getItem('admin_name') || null);
    
    const recovery_code = ref(null);
    const error = ref(null);
    const initialized = ref(false);
    const checkingAuth = ref(false);

    // Getters
    const isAuthenticated = computed(() => !!token.value);
    const getShopId = computed(() => shopId.value);
    const getShopName = computed(() => shopName.value);
    const getShopImage = computed(() => shopImage.value);
    const getAdminId = computed(() => adminId.value);
    const getAdminName = computed(() => adminName.value);

    // Actions
    const setShopImage = (image) => {
        shopImage.value = image;
        localStorage.setItem('shop_image', image);
    };

    const login = async (credentials) => {
        error.value = null;
        try {
            const response = await apiClient.post('v1/admin/login', credentials);

            if (response.status === 200) {
                const data = response.data;

                localStorage.setItem('auth_token', data.access_token);
                localStorage.setItem('shop_id', data.shop_id);
                localStorage.setItem('shop_name', data.shop_name);
                localStorage.setItem('shop_image', data.shop_image);
                localStorage.setItem('admin_id', data.admin_id);
                localStorage.setItem('admin_name', data.admin_name);

                token.value = data.access_token;
                shopId.value = data.shop_id;
                shopName.value = data.shop_name;
                shopImage.value = data.shop_image;
                adminId.value = data.admin_id;
                adminName.value = data.admin_name;

                return {
                    success: true,
                    shop_id: data.shop_id,
                    shop_name: data.shop_name,
                    shop_image: data.shop_image,
                    admin_id: data.admin_id,
                    admin_name: data.admin_name,
                };
            }
        } catch (err) {
            error.value = err.response?.data?.message ||
                err.message ||
                'Login failed. Please try again.';
            throw error.value;
        }
    };

    const shopRegistration = async (credentials) => {
        error.value = null;
        try {
            const response = await apiClient.post('v1/public/shop-registration', credentials);

            if (response.status === 200) {
                const data = response.data;

                // Clear any existing auth first
                clearAuth();

                localStorage.setItem('auth_token', data.access_token);
                localStorage.setItem('shop_id', data.shop_id);
                localStorage.setItem('shop_name', data.shop_name);
                localStorage.setItem('shop_image', data.shop_image);
                localStorage.setItem('admin_id', data.admin_id);
                localStorage.setItem('admin_name', data.admin_name);

                token.value = data.access_token;
                shopId.value = data.shop_id;
                shopName.value = data.shop_name;
                shopImage.value = data.shop_image;
                adminId.value = data.admin_id;
                adminName.value = data.admin_name;

                return {
                    success: data.success,
                    message: data.message,
                    shop_id: data.shop_id,
                    shop_name: data.shop_name,
                    shop_image: data.shop_image,
                    admin_id: data.admin_id,
                    admin_name: data.admin_name,
                };
            }
        } catch (err) {
            error.value = err.response?.data?.message ||
                err.message ||
                'Registration failed. Please try again.';
            throw err;
        }
    };

    const submitEmail = async (credentials) => {
        error.value = null;
        try {
            const response = await apiClient.post('v1/public/verify-email', credentials);

            if (response.status === 200) {
                const data = response.data;

                recovery_code.value = data.recovery_code;

                return {
                    success: true,
                    recovery_code: data.recovery_code,
                };
            }
        } catch (err) {
            error.value = err.response?.data?.message ||
                err.message ||
                'Verifying email failed. Please try again.';
            throw new Error(error.value);
        }
    };

    const submitRecoveryCode = async (credentials) => {
        error.value = null;
        try {
            const response = await apiClient.post('v1/public/verify-recovery-code', credentials);
            const data = response.data;
            if (response.status === 200) {
                return {
                    success: true,
                    data: data
                };
            }
        } catch (err) {
            error.value = err.response?.data?.message ||
                err.message ||
                'Recovery code failed. Please try again.';
            throw new Error(error.value);
        }
    };

    const submitNewPassword = async (credentials) => {
        error.value = null;
        try {
            const response = await apiClient.post('v1/public/recover-account', credentials);
            if (response.status === 200) {
                const data = response.data;

                localStorage.setItem('auth_token', data.access_token);
                localStorage.setItem('shop_id', data.shop_id);
                localStorage.setItem('shop_name', data.shop_name);
                localStorage.setItem('shop_image', data.shop_image);
                localStorage.setItem('admin_id', data.admin_id);
                localStorage.setItem('admin_name', data.admin_name);

                token.value = data.access_token;
                shopId.value = data.shop_id;
                shopName.value = data.shop_name;
                shopImage.value = data.shop_image;
                adminId.value = data.admin_id;
                adminName.value = data.admin_name;

                return {
                    success: true,
                    data: data,
                    shop_id: data.shop_id,
                    shop_name: data.shop_name,
                    shop_image: data.shop_image,
                    admin_id: data.admin_id,
                    admin_name: data.admin_name,
                };
            }
        } catch (err) {
            error.value = err.response?.data?.message ||
                err.message ||
                'Recovery code failed. Please try again.';
            throw new Error(error.value);
        }
    };

    const clearAuth = () => {
        token.value = null;
        shopId.value = null;
        shopName.value = null;
        shopImage.value = null;
        adminId.value = null;
        adminName.value = null;
        error.value = null;

        localStorage.removeItem('auth_token');
        localStorage.removeItem('shop_id');
        localStorage.removeItem('shop_name');
        localStorage.removeItem('shop_image');
        localStorage.removeItem('admin_id');
        localStorage.removeItem('admin_name');
    };

    const logout = async (redirect = true) => {
        const currentToken = token.value;

        clearAuth();

        try {
            if (currentToken) {
                await Promise.race([
                    apiClient.post('v1/admin/logout', null, {
                        headers: {
                            Authorization: `Bearer ${currentToken}`
                        }
                    }),
                    new Promise((_, reject) => setTimeout(() => reject(new Error('Timeout')), 2000))
                ]).catch(err => {
                    console.error('Logout API error:', err);
                });
            }
        } catch (err) {
            console.error('Logout error:', err);
        } finally {
            if (redirect) {
                window.location.href = '/';
            }
        }
    };

    const validateToken = async () => {
        if (!token.value) return false;

        try {
            const response = await apiClient.get('v1/admin/verify-token', {
                headers: {
                    Authorization: `Bearer ${token.value}`
                },
                timeout: 5000
            });

            return response.status === 200;
        } catch (err) {
            console.error('Token validation failed:', err);
            if (err.response?.status === 401 || err.response?.status === 403) {
                clearAuth();
            }
            return false;
        }
    };

    const checkAuth = async () => {
        if (checkingAuth.value) return false;

        checkingAuth.value = true;

        try {
            if (!token.value) {
                clearAuth();
                return false;
            }

            const isValid = await validateToken();

            if (!isValid) {
                clearAuth();
                return false;
            }

            return true;
        } catch (err) {
            console.error('Auth check error:', err);
            clearAuth();
            return false;
        } finally {
            checkingAuth.value = false;
            initialized.value = true;
        }
    };

    const syncFromLocalStorage = () => {
        const storedToken = localStorage.getItem('auth_token');
        const storedShopId = localStorage.getItem('shop_id');
        const storedShopName = localStorage.getItem('shop_name');
        const storedShopImage = localStorage.getItem('shop_image');
        const storedAdminId = localStorage.getItem('admin_id');
        const storedAdminName = localStorage.getItem('admin_name');

        if (storedToken && storedShopId && storedShopName && storedShopImage && storedAdminId && storedAdminName) {
            token.value = storedToken;
            shopId.value = storedShopId;
            shopName.value = storedShopName;
            shopImage.value = storedShopImage;
            adminId.value = storedAdminId;
            adminName.value = storedAdminName;
        } else {
            clearAuth();
        }

        initialized.value = true;
    };

    syncFromLocalStorage();

    return {
        token,
        shopId,
        shopName,
        shopImage,
        adminId,
        adminName,
        error,
        initialized,
        checkingAuth,
        isAuthenticated,
        getShopId,
        getShopName,
        getShopImage,
        getAdminId,
        getAdminName,
        login,
        shopRegistration,
        submitEmail,
        submitRecoveryCode,
        submitNewPassword,
        logout,
        checkAuth,
        validateToken,
        clearAuth,
        syncFromLocalStorage,
        setShopImage,
    };
});